import { injectable } from "inversify";
import type { IEmploymentHttpClient } from "../interfaces/IEmploymentService";
import type { IJobPositionResource, PlatsbankenJobAdStatusResponse } from "../interfaces/IJobPositionResource";
import {
  ApplicantExportRequest,
  BlogPostResponse,
  CreateBlogPostResponse,
  MetaData,
  GetApplicantQuestionaireRequest,
  Skill,
  SkillCategory,
  UpsertApplicantQuestionaireRequest,
  CreateBlogPostRequest,
  CreateApplicantPDFExportRequest,
} from "../models/dtos";
import {
  ApplicantPositionResponse,
  PositionResponse,
  SendEmailToOneRecipientRequest,
  SendEmailRequest,
} from "../models/dtos";
import PositionType from "@/features/positionpartners/models/PositionPartner";
import PositionPartner from "@/features/containerpartners/models/ContainerPartner";
import { PositionPartnerBuilderResponse } from "@/features/containerpartners/models/ContainerPartner";
import GeneratePosition from "@/features/shared/models/GeneratePosition";

@injectable()
export class JobPositionResource implements IJobPositionResource {
  _client: IEmploymentHttpClient;
  constructor(client: IEmploymentHttpClient) {
    this._client = client;
  }
  async createApplicantPDFExport(
    item: CreateApplicantPDFExportRequest[]
  ): Promise<any> {
    const data = {
      action: "create_ApplicantPDFExport",
      data: {
        items: item,
      },
    };
    return await this._client.post(data);
  }
  async createDemoApplicants(item: GeneratePosition): Promise<void> {
    const data = {
      action: "create_positionDemoApplicants",
      data: {
        containerId: item.containerId,
        positionId: item.position.id,
        defaultInterest: item.defaultInterest,
        defaultProcess: item.defaultProcess,
        amount: item.count,
      },
    };
    await this._client.post(data);
  }
  async getPositionPartnersBuilder(): Promise<
    PositionPartnerBuilderResponse[]
  > {
    const data = {
      action: "get_positionsPartnersBuilderQuery",
      data: {},
    };
    const x = await this._client.post(data);
    return x.data;
  }
  async setPositionPartnersSettings(
    containerId: number,
    v: PositionPartner[]
  ): Promise<any> {
    const data = {
      action: "update_positionPartnerSetting",
      data: {
        containerId: containerId,
        settings: v,
      },
    };
    const x = await this._client.post(data);
    return x.data;
  }
  async setPositionPartners(
    containerId: number,
    v: PositionPartner[]
  ): Promise<any> {
    const data = {
      action: "update_PositionPartner",
      data: {
        containerId: containerId,
        settings: v,
      },
    };
    const x = await this._client.post(data);
    return x.data;
  }
  async getApplicantPositionByLabelId(
    containerId: number,
    id: number,
    positionId = -1,
    filter: string,
    filterInclude: boolean
  ): Promise<ApplicantPositionResponse[]> {
    const data = {
      action: "get_applicantPositionByLabel",
      data: {
        containerId: containerId,
        labelId: id,
        positionId: positionId,
        filter: filter,
        filterInclude: filterInclude,
      },
    };
    return await this._client.post(data);
  }
  async getPositionsCard(containerId: number): Promise<PositionResponse[]> {
    const data = {
      action: "get_PositionsCards",
      data: {
        containerId: containerId,
      },
    };
    const x = await this._client.post(data);
    return x.data;
  }
  async getPositionPartnersSettings(
    containerId: number
  ): Promise<PositionPartner[]> {
    const data = {
      action: "get_positionsPartnersSetting",
      data: {
        containerId: containerId,
      },
    };
    const x = await this._client.post(data);
    return x.data.map(
      (v: PositionPartner) =>
        new PositionPartner(v.name, v.id, v.typeId, v.data, v.isChecked)
    );
  }
  async getPositionPartnersByPosition(
    containerId: number,
    positionId: number
  ): Promise<PositionPartner[]> {
    const data = {
      action: "get_PositionsPartnersByPositionId",
      data: {
        containerId: containerId,
        positionId: positionId,
      },
    };
    const x = await this._client.post(data);
    return x.data.map(
      (v: PositionPartner) =>
        new PositionPartner(v.name, v.id, v.typeId, v.data, v.isChecked)
    );
  }
  async registerAccount(
    email: string,
    password: string,
    confirmPassword: string
  ): Promise<any> {
    const data = {
      email: email,
      password: password,
      confirmPassword: confirmPassword,
    };

    const response = await this._client.postUrl("/api/account/register", data);
    return response;
  }
  async updateApplicant(payload: any) {
    const data = {
      action: "update_jobApplicant",
      data: payload,
    };
    return await this._client.post(data);
  }
  async createApplicant(email: string, positionId: number, metadata: MetaData) {
    const data = {
      action: "create_jobApplication",
      data: {
        email: email,
        positionId: positionId,
        metadata: metadata,
        isManuallyCreated: true, // Mark as manually created from admin interface
      },
    };
    return await this._client.post(data);
  }
  async getApplicantsByPosition(id: number, labelId: number) {
    const data = {
      action: "get_applicantPositionByPosition",
      data: {
        positionId: id,
        labelId: labelId,
      },
    };
    const response = await this._client.post(data);
    return response.data as ApplicantPositionResponse[];
  }

  async getFilteredApplicants(id: number, key: string, labelId: number) {
    const data = {
      action: "get_applicantPositionByPosition",
      data: {
        positionId: id,
        filter: key,
        labelId: labelId,
      },
    };
    const response = await this._client.post(data);
    return response.data as ApplicantPositionResponse[];
  }

  async getApplicantPosition(id: number) {
    const data = {
      action: "get_applicantPosition",
      data: {
        id: id,
      },
    };
    const response = await this._client.post(data);
    return response.data as ApplicantPositionResponse;
  }
  async getApplicantPositions(containerId: number, email: string) {
    const data = {
      action: "get_applicantPositions",
      data: {
        containerId: containerId,
        email: email,
      },
    };
    const response = await this._client.post(data);
    return response.data as ApplicantPositionResponse[];
  }
  async exportApplicants(payload: ApplicantExportRequest) {
    const data = {
      action: "create_applicantsExport",
      data: {
        containerId: payload.containerId,
        positionId: payload.positionId,
        labelId: payload.labelId,
        filter: payload.filter,
      },
    };
    return await this._client.post(data);
  }
  async exportStatistics(payload: any) {
    const data = {
      action: "create_statisticsExport",
      data: payload,
    };
    return await this._client.post(data);
  }

  async GetBlogPosts() {
    const data = {
      action: "get_blogPosts",
      data: {},
    };
    const response = await this._client.post(data);
    return response.data as BlogPostResponse[];
  }
  async CreateBlogPost(payload: CreateBlogPostRequest) {
    const data = {
      action: "create_blogPost",
      data: {
        id: payload.id,
        title: payload.title,
        body: payload.body,
      },
    };
    const response = await this._client.post(data);
    return response.data as CreateBlogPostResponse;
  }
  async deleteBlogPost(id: string) {
    const data = {
      action: "delete_blogPost",
      data: {
        id: id,
      },
    };
    return this._client.post(data);
  }
  async getById(id: number) {
    const data = {
      action: "get_position",
      data: {
        id: id,
      },
    };
    const response = await this._client.post(data);
    return response.data as PositionResponse;
  }

  async updateJobApplicationStatus(labelId: number, ids: number[]) {
    const data = {
      action: "update_jobApplicationStatus",
      data: {
        labelId: labelId,
        ids: ids,
      },
    };
    return this._client.post(data);
  }

  async setPresentedToClient(applicantPositionId: number, presented: boolean) {
    const data = {
      action: "set_presentedToClient",
      data: {
        applicantPositionId: applicantPositionId,
        presented: presented,
      },
    };
    return this._client.post(data);
  }
  async setApplicantMedflyttare(
    applicantPositionId: number,
    isMedflyttare: boolean,
    status?: string,
    note?: string,
    relocationDate?: string
  ) {
    const data = {
      action: "set_applicantMedflyttare",
      data: {
        applicantPositionId: applicantPositionId,
        isMedflyttare: isMedflyttare,
        status: status,
        note: note,
        relocationDate: relocationDate,
      },
    };
    return this._client.post(data);
  }
  async deletePosition(id: number) {
    const data = {
      action: "delete_position",
      data: {
        id: id,
      },
    };
    return this._client.post(data);
  }
  async deleteJobapplication(positionId: number, id: number) {
    const data = {
      action: "delete_jobapplication",
      data: {
        positionId: positionId,
        id: id,
      },
    };
    return this._client.post(data);
  }
  async createApplicantNote(id: number, note: string) {
    const data = {
      action: "create_JobApplicantNote",
      data: {
        id: id,
        note: note,
      },
    };
    return this._client.post(data);
  }
  async sendEmailToOneRecipient(payload: SendEmailToOneRecipientRequest) {
    const data = {
      action: "send_email",
      data: {
        applicantId: payload.applicantId,
        email: payload.email,
        subject: payload.subject,
        body: payload.body,
      },
    };
    return this._client.post(data);
  }
  async sendEmailToApplicants(payload: SendEmailRequest) {
    const data = {
      action: "send_emailToApplicants",
      data: {
        positionId: payload.positionId,
        recipients: payload.recipients,
        subject: payload.subject,
        body: payload.body,
      },
    };
    return this._client.post(data);
  }
  async getPositionSkills(containerId: number, positionId: number) {
    const data = {
      action: "get_skillPositions",
      data: {
        containerId: containerId,
        positionId: positionId,
      },
    };
    const response = await this._client.post(data);
    return response.data as SkillCategory[];
  }
  async addPositionSkill(
    containerId: number,
    positionId: number,
    skillId: string
  ) {
    const data = {
      action: "add_skillPosition",
      data: {
        containerId: containerId,
        positionId: positionId,
        skillId: skillId,
      },
    };
    const response = await this._client.post(data);
    return response.data as SkillCategory[];
  }
  async removePositionSkill(
    containerId: number,
    positionId: number,
    skillId: string
  ) {
    const data = {
      action: "delete_skillPosition",
      data: {
        containerId: containerId,
        positionId: positionId,
        skillId: skillId,
      },
    };
    const response = await this._client.post(data);
    return response.data as SkillCategory[];
  }
  async getApplicantQuestionaire(payload: GetApplicantQuestionaireRequest) {
    const data = {
      action: "get_applicantSkillsAnswer",
      data: {
        applicantId: payload.applicantId,
        containerId: payload.containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data as Skill[];
  }
  async updateApplicantQuestionaire(
    payload: UpsertApplicantQuestionaireRequest
  ) {
    const data = {
      action: "create_applicantAnswer",
      data: {
        applicantId: payload.applicantId,
        containerId: payload.containerId,
        skills: payload.skills,
      },
    };
    const response = await this._client.post(data);
    return response.data as Skill[];
  }
  async archivePosition(positionId: number, archive: boolean): Promise<any> {
    const data = {
      action: "update_PositionArchivedStatus",
      data: {
        positionId: positionId,
        isArchived: archive,
      },
    };
    return this._client.post(data);
  }

  async getPlatsbankenJobAdStatus(
    containerId: number,
    positionId: number
  ): Promise<PlatsbankenJobAdStatusResponse> {
    const data = {
      action: "get_PlatsbankenJobAdStatus",
      data: {
        containerId: containerId,
        positionId: positionId,
      },
    };
    const response = await this._client.post(data);
    return response.data as PlatsbankenJobAdStatusResponse;
  }

  async getEmailStatusSummary(
    applicationId: string,
    positionId?: number,
    includeRecentEmails?: boolean,
    recentEmailsLimit?: number
  ): Promise<any> {
    const data = {
      action: "GetEmailStatusSummaryQuery",
      data: {
        applicationId: applicationId,
        positionId: positionId,
        includeRecentEmails: includeRecentEmails ?? true,
        recentEmailsLimit: recentEmailsLimit ?? 5,
      },
    };
    const response = await this._client.post(data);
    return response.data;
  }

  async getEmailTracking(
    applicationId?: string,
    positionId?: number,
    containerId?: number,
    emailType?: string,
    emailAddress?: string,
    limit?: number
  ): Promise<any> {
    const data = {
      action: "GetEmailTrackingQuery",
      data: {
        applicationId: applicationId,
        positionId: positionId,
        containerId: containerId,
        emailType: emailType,
        emailAddress: emailAddress,
        limit: limit ?? 50,
      },
    };
    const response = await this._client.post(data);
    return response.data;
  }

  async getApplicantAuditLog(
    applicantPositionId: number,
    skip?: number,
    take?: number
  ): Promise<any> {
    const data = {
      action: "get_applicantAuditLog",
      data: {
        applicantPositionId: applicantPositionId,
        skip: skip ?? 0,
        take: take ?? 100,
      },
    };
    const response = await this._client.post(data);
    return response.data;
  }
}
